'use client'

import { useCallback, useContext, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import isEqual from 'lodash/isEqual'
import isPlainObject from 'lodash/isPlainObject'
import mergeWith from 'lodash/mergeWith'

import { isAnyScreenFeatureKey, screenFeatureKeys } from '@/lib/query/keys/screen-features'

import { ScreenFeatureError } from './errors'
import { freshness } from './freshness'
import {
  registry,
  type ScreenFeatureChange,
  type ScreenFeatureName,
  type ScreenFeatureValue,
  type SystemScopedName
} from './registry'
import type { ScreenFeatureEntry } from './registry/entry'
import { ScreenFeatureTransportContext, type ScreenFeatureResource, type ScreenFeatureTransport } from './transport'

/**
 * The explicit fleet-default target. `undefined` never means "the fleet
 * default": on the server a PATCH with no `school_id` rewrites the row every
 * school inherits, so that write needs this sentinel, and only `systemScope`
 * entries accept it (KTD1).
 */
export const SYSTEM_DEFAULTS = Symbol('screen-features.system-defaults')

type SchoolTarget<N extends ScreenFeatureName> =
  string | undefined | (N extends SystemScopedName ? typeof SYSTEM_DEFAULTS : never)

type AnyEntry = ScreenFeatureEntry<
  ScreenFeatureResource,
  any,
  Record<string, unknown>,
  Record<string, unknown>,
  Record<string, unknown>
>

/** `serverValue` is the server's merged block, parsed. `undefined`: no block (absentIsEmpty). */
interface CachedEntry {
  serverValue: Record<string, unknown> | undefined
}

interface SaveVariables {
  target: string | typeof SYSTEM_DEFAULTS
  change: Record<string, unknown>
}

/**
 * The server's merge rule (`customMerge`,
 * `apps/backend/src/services/config/service.ts:63`): objects merge, arrays
 * replace, scalars replace.
 */
function customMerge(_objValue: unknown, srcValue: unknown) {
  return Array.isArray(srcValue) ? srcValue : undefined
}

function resolveValue(entry: AnyEntry, serverValue: Record<string, unknown> | undefined): Record<string, unknown> {
  const base = serverValue === undefined ? {} : entry.toValue ? entry.toValue(serverValue) : serverValue
  return mergeWith({}, entry.fallback, base, customMerge)
}

/**
 * Marks a field the save removes: a change key explicitly set to `undefined`
 * where the current value has that key ("reset to default", "switch the
 * button off"). It survives the merge into the body and is stripped from it
 * afterwards, so the stored block no longer carries the field.
 */
const REMOVE = Symbol('screen-features.remove')

/** A key whose value did not change: not part of the diff. */
const UNCHANGED = Symbol('screen-features.unchanged')

function hasRemoval(value: unknown): boolean {
  if (value === REMOVE) return true
  return isPlainObject(value) && Object.values(value as Record<string, unknown>).some(hasRemoval)
}

function stripRemovals(value: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, field] of Object.entries(value)) {
    if (field === REMOVE) continue
    out[key] = isPlainObject(field) ? stripRemovals(field as Record<string, unknown>) : field
  }
  return out
}

/** The block without the `sendOnlyWhenChanged` keys this save did not change. */
function omitUnchanged(
  block: Record<string, unknown>,
  keys: readonly string[] | undefined,
  diff: Record<string, unknown>
): Record<string, unknown> {
  if (!keys?.length) return block
  const out = { ...block }
  for (const key of keys) if (!(key in diff)) delete out[key]
  return out
}

/**
 * The keys of `change` whose value differs from `current`, recursing into plain
 * objects. A panel may pass its whole draft: an unchanged field, including one
 * that only shows the fallback, is not a change and never reaches the server.
 * A key set to `undefined` that `current` has is a removal (`REMOVE`).
 */
function changedOnly(change: Record<string, unknown>, current: unknown): Record<string, unknown> {
  const before = isPlainObject(current) ? (current as Record<string, unknown>) : {}
  const out: Record<string, unknown> = {}
  for (const [key, next] of Object.entries(change)) {
    const delta = changeOf(next, before[key])
    if (delta !== UNCHANGED) out[key] = delta
  }
  return out
}

/** One key's change: `UNCHANGED`, `REMOVE`, a nested diff, or the new value. */
function changeOf(next: unknown, prev: unknown): unknown {
  if (next === undefined) return prev === undefined ? UNCHANGED : REMOVE
  if (isEqual(next, prev)) return UNCHANGED
  if (isPlainObject(next) && isPlainObject(prev)) {
    const nested = changedOnly(next as Record<string, unknown>, prev)
    return Object.keys(nested).length > 0 ? nested : UNCHANGED
  }
  return next
}

async function readEntry(
  transport: ScreenFeatureTransport,
  entry: AnyEntry,
  schoolId: string | undefined
): Promise<CachedEntry> {
  let data: unknown
  try {
    data = await transport.get(entry.resource, entry.wireName, schoolId)
  } catch (error) {
    if (error instanceof ScreenFeatureError && error.kind === 'not-found' && entry.absentIsEmpty) {
      return { serverValue: undefined }
    }
    throw error
  }
  // Only this entry's schema, never the whole blob. A default `z.object` strips
  // unknown keys, so a newer server's extra field does not fail the read.
  const parsed = entry.schema.safeParse(data)
  if (!parsed.success) {
    throw new ScreenFeatureError('invalid', `The ${entry.wireName} settings the server returned are not readable`)
  }
  return { serverValue: parsed.data as Record<string, unknown> }
}

function asScreenFeatureError(error: unknown): ScreenFeatureError {
  if (error instanceof ScreenFeatureError) return error
  return new ScreenFeatureError('server', error instanceof Error ? error.message : 'Request failed')
}

type ScreenFeatureStatus = 'loading' | 'ready' | 'error' | 'no-school'

/**
 * Read and write one Screen Features entry for a target school (web refactor
 * 02, KTD1). A panel deals only in domain values: keys, endpoint, write mode,
 * fallback and freshness all come from the registry entry.
 *
 * Reads fail closed (KTD7): an errored or unreadable read gives
 * `status: 'error'` and `value: undefined`, never the fallback, and `save`
 * rejects unless `status === 'ready'`, so a panel cannot render defaults and
 * then save them over the school's real values.
 */
export function useScreenFeature<N extends ScreenFeatureName>(
  name: N,
  schoolId: SchoolTarget<N>
): {
  value: ScreenFeatureValue<N> | undefined
  status: ScreenFeatureStatus
  error: ScreenFeatureError | null
  save: (change: ScreenFeatureChange<N>) => Promise<ScreenFeatureValue<N>>
  isSaving: boolean
} {
  const entry = registry[name] as unknown as AnyEntry
  const queryClient = useQueryClient()
  const { useTransport } = useContext(ScreenFeatureTransportContext)
  const transport = useTransport()

  const requested: string | typeof SYSTEM_DEFAULTS | undefined = schoolId
  const isSystem = requested === SYSTEM_DEFAULTS
  // Typecheck already refuses this; the runtime check covers an untyped caller.
  const refusedSystemTarget = isSystem && !entry.systemScope
  const target = requested === SYSTEM_DEFAULTS ? undefined : requested
  const hasTarget = isSystem ? !refusedSystemTarget : target !== undefined
  const policy = freshness[entry.resource]

  const entryKeyFor = (saveTarget: string | typeof SYSTEM_DEFAULTS | undefined) =>
    saveTarget === SYSTEM_DEFAULTS
      ? screenFeatureKeys.systemEntry(entry.resource, entry.wireName)
      : screenFeatureKeys.entry(saveTarget ?? null, entry.resource, entry.wireName)

  const query = useQuery({
    queryKey: entryKeyFor(requested),
    queryFn: () => readEntry(transport as ScreenFeatureTransport, entry, target),
    enabled: hasTarget && transport !== null,
    staleTime: policy.staleTime,
    refetchOnMount: policy.refetchOnMount,
    refetchOnWindowFocus: policy.refetchOnWindowFocus,
    gcTime: policy.gcTime
  })

  const mutation = useMutation({
    // Saves of one entry for one target run one after another, and each reads
    // the cache when it starts, not when it was clicked. Two overlapping saves
    // would otherwise both build on the same stale value: the second replace
    // body would drop the first's change on the server, and under the global
    // set-cached policy the second cache write would hide it.
    scope: { id: JSON.stringify(entryKeyFor(requested)) },
    mutationFn: async ({ target: saveTarget, change }: SaveVariables) => {
      if (!transport) throw new ScreenFeatureError('rejected', 'Not signed in')
      const current = queryClient.getQueryData<CachedEntry>(entryKeyFor(saveTarget))
      if (!current) throw new ScreenFeatureError('rejected', 'Settings have not loaded yet')
      const { serverValue } = current
      const value = resolveValue(entry, serverValue)
      const diff = changedOnly(change, value)
      if (Object.keys(diff).length === 0) return { serverValue, value, changed: false }

      const wireDiff = entry.toWire ? entry.toWire(diff, value) : diff
      const screen = entry.resource === 'screen'
      const removes = hasRemoval(wireDiff)
      // The server deep-merges a sparse patch, so it has no way to drop a field.
      // Screen: the full write stores the same merged config the partial one
      // does (`writeScreenBlock`, `apps/backend/src/services/config/screen-features.ts:391`),
      // so a removal goes out as that write and gives up only the in-CAS merge,
      // for this one save. Global: the full write would snapshot the merged
      // blob into the school's sparse row, so refuse rather than report a
      // removal as saved.
      const sparse = entry.writeMode === 'sparse' && !(screen && removes)
      if (sparse && removes) {
        throw new ScreenFeatureError('rejected', `${entry.wireName} settings cannot remove a field`)
      }
      // Sparse sends only the changed keys; the server merges them in. Replace
      // sends the server's merged block with them merged on top, so a
      // fallback-only field is never written. The one exception: with no block
      // on the server (absentIsEmpty) there is no fleet value to inherit, and
      // the strict whole-object write refuses a block missing its required
      // fields (chat's `visibleForRoles`, calendar's `enabled` / `access`), so
      // that first write is built on the fallback. A screen sparse patch meets
      // the same strict parse (merged onto `{}`), so it carries the fallback
      // too; a global one is checked with `.partial()` and stays bare.
      const base = serverValue ?? (entry.fallback as Record<string, unknown>)
      const patch =
        screen && serverValue === undefined
          ? (mergeWith({}, entry.fallback, wireDiff, customMerge) as Record<string, unknown>)
          : wireDiff
      const nextServerValue = sparse
        ? (mergeWith({}, serverValue ?? {}, patch, customMerge) as Record<string, unknown>)
        : stripRemovals(mergeWith({}, base, wireDiff, customMerge))
      const body = sparse ? patch : omitUnchanged(nextServerValue, entry.sendOnlyWhenChanged, wireDiff)
      await transport.patch(entry.resource, entry.wireName, body, {
        schoolId: saveTarget === SYSTEM_DEFAULTS ? undefined : saveTarget,
        partial: sparse
      })

      return { serverValue: nextServerValue, value: resolveValue(entry, nextServerValue), changed: true }
    },
    // The school comes from the variables, never this hook's closure.
    onSuccess: async (result, { target: saveTarget }) => {
      if (!result.changed) return
      if (saveTarget === SYSTEM_DEFAULTS) {
        // School entries and fleet defaults share no key prefix.
        await queryClient.invalidateQueries({ predicate: q => isAnyScreenFeatureKey(q.queryKey) })
        return
      }
      const resourceKey = screenFeatureKeys.resource(saveTarget, entry.resource)
      if (policy.afterSave === 'invalidate') {
        await queryClient.invalidateQueries({ queryKey: resourceKey })
        return
      }
      // The global read may still come from a replica, so a refetch now can
      // answer the pre-save value and the toggle "reverts". Cache what the save
      // wrote and refresh only the other entries of the resource.
      const entryKey = screenFeatureKeys.entry(saveTarget, entry.resource, entry.wireName)
      queryClient.setQueryData<CachedEntry>(entryKey, { serverValue: result.serverValue })
      await queryClient.invalidateQueries({ queryKey: resourceKey, predicate: q => !isEqual(q.queryKey, entryKey) })
    }
  })

  const cached = query.data
  const status: ScreenFeatureStatus =
    requested === undefined
      ? 'no-school'
      : refusedSystemTarget || query.isError
        ? 'error'
        : query.isSuccess
          ? 'ready'
          : 'loading'

  const value = useMemo(
    () => (status === 'ready' && cached ? resolveValue(entry, cached.serverValue) : undefined),
    [status, cached, entry]
  )

  const error = useMemo(() => {
    if (refusedSystemTarget) {
      return new ScreenFeatureError('rejected', `${name} does not accept the fleet-default target`)
    }
    return query.error ? asScreenFeatureError(query.error) : null
  }, [refusedSystemTarget, query.error, name])

  const { mutateAsync } = mutation
  const save = useCallback(
    async (change: ScreenFeatureChange<N>) => {
      if (requested === undefined) throw new ScreenFeatureError('rejected', 'No school selected')
      if (status !== 'ready' || !cached || !value) {
        throw error ?? new ScreenFeatureError('rejected', 'Settings have not loaded yet')
      }
      try {
        const result = await mutateAsync({
          target: requested,
          change: change as unknown as Record<string, unknown>
        })
        return result.value as unknown as ScreenFeatureValue<N>
      } catch (saveError) {
        throw asScreenFeatureError(saveError)
      }
    },
    [requested, status, cached, value, error, mutateAsync]
  )

  return {
    value: value as unknown as ScreenFeatureValue<N> | undefined,
    status,
    error,
    save,
    isSaving: mutation.isPending
  }
}
